import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Loader2 } from "lucide-react";

const CATEGORIES = ["All", "Family", "Honeymoon", "Adventure", "Group", "Luxury", "Budget"];
const DURATIONS = [
  { label: "Any duration", value: "any" },
  { label: "3-4 days", value: "3-4" },
  { label: "5-6 days", value: "5-6" },
  { label: "7+ days", value: "7-99" },
];

export default function PackagesList() {
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState("any");
  const [maxPrice, setMaxPrice] = useState(100000);

  // Category is derived from URL — so clicking any footer link like
  // /packages?category=Honeymoon updates the filter in-place.
  const category = params.get("category") || "All";
  const setCategory = (v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "All") next.delete("category");
    else next.set("category", v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category && category !== "All") q.category = category;
    if (duration !== "any") {
      const [mn, mx] = duration.split("-").map(Number);
      q.min_days = mn;
      q.max_days = mx;
    }
    if (maxPrice) q.max_price = maxPrice;
    api
      .get("/packages", { params: q })
      .then((r) => setPackages(r.data || []))
      .finally(() => setLoading(false));
  }, [category, duration, maxPrice]);

  return (
    <div data-testid="packages-page">
      {/* Header banner */}
      <section className="bg-[color:var(--brand-surface-alt)] border-b border-[color:var(--brand-border)]">
        <div className="container-narrow py-16 md:py-24">
          <div className="overline">All journeys</div>
          <h1 className="font-heading text-5xl sm:text-6xl mt-3 leading-tight max-w-3xl">
            Every itinerary is human-designed. Pick one, or ask us to remix it.
          </h1>
          {category !== "All" && (
            <p
              className="mt-4 text-sm text-[color:var(--brand-text-soft)]"
              data-testid="active-category-note"
            >
              Showing <span className="font-medium text-[color:var(--brand-text)]">{category}</span> packages ·{" "}
              <button
                type="button"
                className="link-underline"
                onClick={() => setCategory("All")}
                data-testid="clear-category"
              >
                clear filter
              </button>
            </p>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className="container-narrow py-10 border-b border-[color:var(--brand-border)]">
        <div className="grid md:grid-cols-3 gap-6" data-testid="filters-bar">
          <div>
            <div className="overline mb-2">Category</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="overline mb-2">Duration</div>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="filter-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="overline mb-2">Max budget · ₹{maxPrice.toLocaleString("en-IN")}</div>
            <Slider
              value={[maxPrice]}
              onValueChange={(v) => setMaxPrice(v[0])}
              min={10000}
              max={100000}
              step={1000}
              data-testid="filter-price"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-narrow py-12">
        {loading ? (
          <div className="flex justify-center py-24" data-testid="loading">
            <Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-primary)]" />
          </div>
        ) : packages.length === 0 ? (
          <div
            className="text-center py-24 text-[color:var(--brand-text-soft)]"
            data-testid="empty-state"
          >
            No packages match those filters. Try widening the budget or duration.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-grid">
            {packages.map((p) => (
              <PackageCard key={p.id} pkg={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
